import { ClipboardList, List } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import useCaseService from '../../services/useCaseService';
import { UseCaseSummary } from '../../types';
import { PageLayout } from '../../components/Layout/PageLayout';
import { UseCaseMatrixCard } from '../../components/Dashboard';
import { UseCasesKPIs } from '../../components/UseCases/UseCasesKPIs';
import { DecisionMatrixCard, MatrixBuckets } from '../../components/UseCases/DecisionMatrixCard';
import { Button } from '../../components/Common/button';

const UseCaseDashboard: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const uncategorizedLabel = t('useCases.uncategorized');

  // Data
  const [useCases, setUseCases] = useState<UseCaseSummary[]>([]);

  // Category filter for DecisionMatrixCard only
  const [categoryFilter, setCategoryFilter] = useState<string | 'all'>('all');

  useEffect(() => {
    let ignore = false;
    const run = async () => {
      try {
        const data = await useCaseService.list();
        if (!ignore) setUseCases(data);
      } catch (e: any) {
        console.error("Erreur lors du chargement des cas d'usage:", e);
      }
    };
    run();
    return () => { ignore = true; };
  }, []);

  // Derived data: categories present in use cases
  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const uc of useCases) {
      const raw = (uc.category || '').trim();
      const key = raw || uncategorizedLabel;
      counts[key] = (counts[key] || 0) + 1;
    }
    return counts;
  }, [useCases, uncategorizedLabel]);

  const sortedCategories = useMemo(
    () => Object.entries(categoryCounts).sort((a, b) => a[0].localeCompare(b[0])),
    [categoryCounts],
  );

  // Filter use cases by category only (for DecisionMatrixCard)
  const filteredByCategory = useMemo(() => {
    if (categoryFilter === 'all') return useCases;
    return useCases.filter((u) => {
      const category = (u.category || uncategorizedLabel).trim() || uncategorizedLabel;
      return category === categoryFilter;
    });
  }, [useCases, categoryFilter, uncategorizedLabel]);

  // Derived metrics for dashboard cards (based on all use cases, no filtering)
  const totalUseCases = useCases.length;
  const completedUseCases = useCases.filter((u) => u.status === 'Completed').length;
  const averageImpact =
    useCases.length > 0
      ? Math.round(
          (useCases.reduce((sum, u) => sum + (typeof u.value_score === 'number' ? u.value_score : 0), 0) /
            useCases.length) *
            10,
        ) / 10
      : null;
  const activeCategories = sortedCategories.length;

  // Helper to bucket use cases into quadrants for the matrix (based on category-filtered data)
  const matrixBuckets = useMemo((): MatrixBuckets => {
    const buckets: MatrixBuckets = {
      quickWins: [],
      strategic: [],
      easy: [],
      reconsider: [],
    };
    for (const uc of filteredByCategory) {
      const impact = uc.value_score ?? null;
      const complexity = uc.complexity_score ?? null;
      if (impact === null || complexity === null) continue;

      const highImpact = impact >= 7;
      const mediumImpact = impact >= 4 && impact < 7;
      const lowImpact = impact < 4;
      const lowComplexity = complexity <= 4;
      const highComplexity = complexity > 4;

      if (highImpact && lowComplexity) {
        buckets.quickWins.push(uc);
      } else if (highImpact && highComplexity) {
        buckets.strategic.push(uc);
      } else if (mediumImpact && lowComplexity) {
        buckets.easy.push(uc);
      } else if (lowImpact || (mediumImpact && highComplexity)) {
        buckets.reconsider.push(uc);
      }
    }
    return buckets;
  }, [filteredByCategory]);

  return (
    <PageLayout
      icon={<ClipboardList className="h-6 w-6" />}
      title={t('useCases.dashboard.title', 'Tableau de bord des cas d\'usage')}
      subtitle={t('useCases.dashboard.subtitle', 'Vue d\'ensemble et analyse des cas d\'usage')}
      headerRight={
        <Button
          onClick={() => navigate('/cas-usage/list')}
          variant="outline"
          size="sm"
        >
          <List className="h-4 w-4 mr-2" />
          {t('useCases.dashboard.viewAll', 'Voir tous les cas d\'usage')}
        </Button>
      }
    >
      {/* First row: KPI cards */}
      <UseCasesKPIs
        totalUseCases={totalUseCases}
        completedUseCases={completedUseCases}
        averageImpact={averageImpact}
        activeCategories={activeCategories}
      />

      {/* Second row: UseCase Matrix Card */}
      <UseCaseMatrixCard title={t('useCases.matrix.title')} />

      {/* Third row: Decision matrix */}
      <DecisionMatrixCard
        matrixBuckets={matrixBuckets}
        categoryFilter={categoryFilter}
        totalUseCases={filteredByCategory.length}
        sortedCategories={sortedCategories}
        onCategoryFilterChange={setCategoryFilter}
      />
    </PageLayout>
  );
};

export default UseCaseDashboard;
